import { Menu, MenuButton, MenuItem, MenuList } from '@reach/menu-button'
import React from 'react'
import { resolve } from 'styled-jsx/css'
import { Chevron } from '../../elements/Chevron'
import { Text } from '../../elements/Text'
import { useBrand } from '../../providers/brandProvider'
import { Color } from '../../utils'
import { IProps as PlanColumnGroupLayoutProps } from '../PlanColumnGroupLayout'
import { IProps as SolutionWithTiersProps } from '../SolutionWithTiers'
import { IHelpText, IProps as TabbedSolutionProps } from './index'
import { MobileMenuBuilderLegacy } from './MobileMenuBuilder-legacy'

interface IMenuSectionHeaderStyles {
  menuTopSectionHeaderStyle: IMenuStyles
  menuMiddleSectionHeaderStyle: IMenuStyles
}

interface IMenuStyles {
  className: string
  styles: string
}

export class MobileMenuBuilderDefault {
  menuSectionHeaderStyles: IMenuSectionHeaderStyles
  menuItemStyles: IMenuStyles
  buttonStyles: IMenuStyles
  menuListStyles: IMenuStyles

  constructor() {
    this.menuSectionHeaderStyles = getMenuSectionHeaderStyles()
    this.menuItemStyles = getMenuItemStyles()
    this.buttonStyles = getMenuButtonStyles()
    this.menuListStyles = getMenuListStyles()
  }

  build(
    { solutions, helpText, showHelp }: TabbedSolutionProps,
    handleDropdownSelection: (n: number) => void,
    selectedSolutionHeading: string,
  ) {
    const menuItems = this.buildSolutionMenuItems(solutions, handleDropdownSelection)
    if (showHelp) {
      this.addSectionHeaders(menuItems, helpText)
    }

    return (
      <div>
        <Menu>
          <div className="menu-button-container">
            {this.buildMenuButton(selectedSolutionHeading)}
          </div>
          <MenuList className={this.menuListStyles.className}>
            <span className="nib" />
            {menuItems}
          </MenuList>
        </Menu>
        <style jsx>
          {`
            .menu-button-container {
              display: flex;
              justify-content: center;
            }
            :global([data-reach-menu]) {
              width: 85%;
              z-index: 99;
            }

            .nib {
              content: '';
              display: block;
              position: absolute;
              top: -7px;
              left: 50%;
              width: 14px;
              height: 14px;
              transform: translateX(-50%) rotate(45deg);
              background: white;
              border-radius: 4px 0px 0px;
              margin: 0;
            }
          `}
        </style>
      </div>
    )
  }

  styles() {
    return [
      this.buttonStyles.styles,
      this.menuListStyles.styles,
      this.menuSectionHeaderStyles.menuMiddleSectionHeaderStyle.styles,
      this.menuSectionHeaderStyles.menuTopSectionHeaderStyle.styles,
      this.menuItemStyles.styles,
    ]
  }

  buildMenuButton(selectedSolutionHeading: string) {
    return (
      <MenuButton className={this.buttonStyles.className}>
        <div className="dropdown-heading">
          <Text size="body-bold" brand="default">
            {selectedSolutionHeading}
          </Text>
        </div>
        <span className="chevron-wrapper">
          <Chevron />
        </span>
        <style jsx>
          {`
            .dropdown-heading {
              vertical-align: middle;
              display: inline-block;
            }
            .chevron-wrapper {
              display: flex;
              padding: 0 14px;
              vertical-align: middle;
              justify-content: flex-end;
              flex-grow: 1;
            }
          `}
        </style>
      </MenuButton>
    )
  }

  buildSolutionMenuItems(
    solutions: (SolutionWithTiersProps | PlanColumnGroupLayoutProps)[],
    handleDropdownSelection: (n: number) => void,
  ) {
    return solutions.map((solutionProps, index) => (
      <MenuItem
        className={this.menuItemStyles.className}
        key={index}
        onSelect={() => handleDropdownSelection(index)}
      >
        <Text size="body-bold" brand="default">
          {(solutionProps as PlanColumnGroupLayoutProps).tabHeading ||
            (solutionProps as SolutionWithTiersProps).heading}
        </Text>
      </MenuItem>
    ))
  }

  addSectionHeaders(menuItems: any[], helpText: IHelpText) {
    menuItems.unshift(
      this.mobileMenuSectionHeader(
        helpText.allInOneHelpText || '',
        this.menuSectionHeaderStyles.menuTopSectionHeaderStyle.className,
      ),
    )
    menuItems.splice(
      2,
      0,
      this.mobileMenuSectionHeader(
        helpText.otherSolutionsHelpText || '',
        this.menuSectionHeaderStyles.menuMiddleSectionHeaderStyle.className,
      ),
    )
  }

  mobileMenuSectionHeader(text: string, className?: string) {
    return (
      <MenuItem className={className} onSelect={() => {}}>
        <Text size="md">{text}</Text>
      </MenuItem>
    )
  }
}

export default function getMenuItemStyles() {
  return resolve`
    {
      padding: 20px;
      [data-reach-menu-item][data-selected] {
        background: ${Color.White};
        color: ${Color.Slate};
      }

      [data-reach-menu-item] {
        padding: 10px 20px;
      }
    }
  `
}

function getMenuListStyles() {
  return resolve`
    {
      background-color: ${Color.White};
      padding: 10px 4px 16px 4px;
      margin-top: 16px;
      border-radius: 6px;
      border: none;
      box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1);
      position: relative;
    }
  `
}

function getMenuButtonStyles() {
  return resolve`
    {
      border: 2px solid ${Color.Slate};
      border-radius: 25px;
      padding: 14px 4px 14px 22px;
      display: flex;
      align-items: center;
      background-color: ${Color.White};
      margin-top: 30px;

      [data-reach-menu-button] {
        width: 85%;
      }
    }
  `
}

function getMenuSectionHeaderStyles() {
  return {
    menuTopSectionHeaderStyle: resolve`{
      [data-reach-menu-item][data-selected] {
        background: ${Color.White};
        color: ${Color.Slate};
        white-space: normal;
      }

      [data-reach-menu-item] {
        white-space: normal;
        cursor: unset;
        padding: unset;
        margin: 16px 20px;
      }
    }`,
    menuMiddleSectionHeaderStyle: resolve`{
      [data-reach-menu-item][data-selected] {
        background: ${Color.White};
        color: ${Color.Slate};
        white-space: normal;
      }

      [data-reach-menu-item] {
        cursor: unset;
        white-space: normal;
        margin: 16px 20px;
        padding: 24px 0 0 0;
        border-top: 1px dotted black;
      }
    }`,
  }
}

export const useMobileMenuBuilder = () => {
  const brand = useBrand()

  if (brand === 'default') {
    return new MobileMenuBuilderDefault()
  }

  return new MobileMenuBuilderLegacy()
}
